import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { parentLogin, getOnboardingStatus, resendVerificationEmail } from '../../services/api';

// ─── Brand tokens (parent interface) ──────────────────────────────────────────

const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceMuted: '#F8FAFC',
  text: '#1E293B',
  textMuted: '#64748B',
  textSubtle: '#94A3B8',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentDarker: '#003D99',
  accentSoft: '#EFF4FF',
  accentBorder: '#C7D7FE',
  border: '#E2E8F0',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT = "'DM Sans', sans-serif";

// ─── Brand mark ───────────────────────────────────────────────────────────────

function LanternBrand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
      <div style={{
        width: 32, height: 32, borderRadius: 9,
        background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDarker} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 2px 8px ${C.accent}55`,
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="5" y="3" width="6" height="8" rx="1" stroke="#fff" strokeWidth="1.4"/>
          <path d="M6.5 3V2h3v1M5 11h6M7 13h2" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </div>
      <span style={{
        fontFamily: HEADING_FONT, fontSize: 18, fontWeight: 700,
        color: C.text, letterSpacing: '-0.01em',
      }}>Lantern</span>
    </div>
  );
}

// ─── Main login ───────────────────────────────────────────────────────────────

function friendlyError(err) {
  const status = err?.response?.status;
  const serverMessage = err?.response?.data?.error;
  if (status === 401) return 'Email or password didn’t match — try again.';
  if (status === 403) return serverMessage || 'Please verify your email before signing in. Check your inbox for the verification link.';
  if (status === 429) return 'Too many attempts. Please wait a few minutes and try again.';
  if (status === 400) return serverMessage || 'Please check your email and password.';
  return 'Something went wrong. Please try again in a moment.';
}

export default function ParentLogin() {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState(null);
  const [errorStatus, setErrorStatus]   = useState(null); // HTTP status of last login error
  const [focused, setFocused]           = useState(null);

  // Resend-verification state — only relevant when errorStatus === 403.
  // 'idle' | 'sending' | 'sent' | 'error'
  const [resendStatus, setResendStatus] = useState('idle');
  const [resendError, setResendError]   = useState('');

  async function handleResendVerification() {
    if (!email.trim()) {
      setResendError('Enter your email above first.');
      setResendStatus('error');
      return;
    }
    setResendStatus('sending');
    setResendError('');
    try {
      await resendVerificationEmail(email.trim());
      setResendStatus('sent');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setResendError('Too many resend attempts. Please wait a few minutes.');
      } else if (status === 400) {
        setResendError(err?.response?.data?.error || 'Please check your email address.');
      } else {
        setResendError('Could not send. Please try again in a moment.');
      }
      setResendStatus('error');
    }
  }

  // Surface error messages handed to us via location.state — typically from the
  // 2FA screen bouncing the parent back when their temp token expired. After
  // consuming, we clear the history state so the next render and refreshes
  // don't replay the message; that secondary run is a harmless no-op.
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setErrorStatus(null);
    setResendStatus('idle');
    setResendError('');

    if (!email.trim() || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await parentLogin(email.trim(), password);
      const data = res.data;

      if (data.requires2FA && data.tempToken) {
        navigate('/parent/2fa', { state: { tempToken: data.tempToken } });
        return;
      }

      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
        // Route new parents through the onboarding wizard. Falls back to the
        // dashboard if the status check fails — better than getting stuck.
        try {
          const statusRes = await getOnboardingStatus();
          navigate(statusRes.data?.onboardingComplete ? '/parent/dashboard' : '/parent/onboarding');
        } catch {
          navigate('/parent/dashboard');
        }
        return;
      }

      setError('Something went wrong. Please try again.');
    } catch (err) {
      setError(friendlyError(err));
      setErrorStatus(err?.response?.status ?? null);
    } finally {
      setSubmitting(false);
    }
  }

  const inputWith = (name) => ({
    border: `1px solid ${focused === name ? C.accent : C.border}`,
    boxShadow: focused === name ? `0 0 0 4px ${C.accent}22` : 'none',
    borderRadius: 10,
    padding: '12px 14px',
    fontFamily: BODY_FONT,
    fontSize: 15,
    color: C.text,
    outline: 'none',
    background: '#fff',
    width: '100%',
    transition: 'border-color 160ms ease, box-shadow 160ms ease',
  });

  return (
    <div style={{
      minHeight: '100vh',
      background: `${C.bg} radial-gradient(ellipse 600px 400px at 50% -10%, ${C.accent}1c 0%, ${C.accent}00 60%)`,
      fontFamily: BODY_FONT,
      color: C.text,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '40px 20px 60px',
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ marginBottom: 24 }}>
          <LanternBrand />
        </div>

        <div style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: '32px 28px',
          boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
        }}>
          <h1 style={{
            margin: 0,
            fontFamily: HEADING_FONT,
            fontSize: 26,
            fontWeight: 700,
            color: C.text,
            letterSpacing: '-0.02em',
          }}>Welcome back</h1>
          <p style={{ margin: '6px 0 24px', fontSize: 14, color: C.textMuted }}>
            Sign in to your Lantern account.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <label htmlFor="parent-email" style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 500,
              marginBottom: 6,
              color: C.text,
            }}>Email</label>
            <input
              id="parent-email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              value={email}
              onChange={(e) => { setError(null); setErrorStatus(null); setResendStatus('idle'); setResendError(''); setEmail(e.target.value); }}
              onFocus={() => setFocused('email')}
              onBlur={() => setFocused(null)}
              placeholder="you@example.com"
              disabled={submitting}
              style={inputWith('email')}
            />

            <label htmlFor="parent-password" style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 500,
              margin: '18px 0 6px',
              color: C.text,
            }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="parent-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setError(null); setPassword(e.target.value); }}
                onFocus={() => setFocused('password')}
                onBlur={() => setFocused(null)}
                placeholder="Your password"
                disabled={submitting}
                style={{ ...inputWith('password'), paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  padding: 6,
                  cursor: 'pointer',
                  color: C.textMuted,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M3 3l14 14M8.5 4.6a8 8 0 0 1 1.5-.1c5 0 8 5.5 8 5.5a14 14 0 0 1-2.2 2.9M6.2 6.2A14 14 0 0 0 2 10s3 5.5 8 5.5c1.4 0 2.6-.3 3.7-.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                    <path d="M8.5 8.5a2 2 0 0 0 2.8 2.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M2 10s3-5.5 8-5.5 8 5.5 8 5.5-3 5.5-8 5.5S2 10 2 10z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                    <circle cx="10" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.6"/>
                  </svg>
                )}
              </button>
            </div>

            {error && (
              <div role="alert" style={{
                marginTop: 16,
                padding: '10px 14px',
                background: C.redSoft,
                border: `1px solid ${C.redBorder}`,
                borderRadius: 10,
                color: C.redText,
                fontSize: 13,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="8" cy="8" r="7" stroke={C.redText} strokeWidth="1.5"/>
                  <path d="M8 4v5M8 11.5v.5" stroke={C.redText} strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {error}
              </div>
            )}

            {errorStatus === 403 && (
              <div style={{ marginTop: 10, fontSize: 13, color: C.textMuted }}>
                {resendStatus === 'sent' ? (
                  <span style={{ color: '#065F46' }}>
                    If an account exists for that address and still needs verification,
                    a new email is on its way.
                  </span>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendStatus === 'sending'}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: C.accent,
                        fontFamily: BODY_FONT,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: resendStatus === 'sending' ? 'not-allowed' : 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      {resendStatus === 'sending' ? 'Sending…' : 'Resend verification email'}
                    </button>
                    {resendError && (
                      <span style={{ marginLeft: 8, color: C.redText }}>{resendError}</span>
                    )}
                  </>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                marginTop: 22,
                width: '100%',
                minHeight: 48,
                padding: '14px 24px',
                background: C.accent,
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontFamily: BODY_FONT,
                fontSize: 15,
                fontWeight: 600,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
                boxShadow: '0 1px 0 rgba(0,61,153,0.4) inset, 0 1px 2px rgba(0,102,255,0.25), 0 4px 12px rgba(0,102,255,0.18)',
                transition: 'background 160ms ease, transform 90ms ease, box-shadow 160ms ease',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              {submitting ? (
                <>
                  <span style={{
                    width: 16, height: 16, borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: '#fff',
                    animation: 'pl-spin 700ms linear infinite',
                  }}/>
                  Signing in&hellip;
                </>
              ) : (
                <>
                  Sign in
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </>
              )}
            </button>

            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <Link
                to="/parent/forgot-password"
                style={{ color: C.accent, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          <style>{`@keyframes pl-spin { to { transform: rotate(360deg); } }`}</style>
        </div>

        <p style={{
          marginTop: 18,
          textAlign: 'center',
          fontSize: 14,
          color: C.textMuted,
        }}>
          New here?{' '}
          <Link
            to="/parent/register"
            style={{ color: C.accent, fontWeight: 600, textDecoration: 'none' }}
          >
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
